// store/modules/candidateCompare.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        comparePages: [],
        currentComparePage: null,
        loading: false,
        error: null
    },
    getters: {
        allComparePages: (state) => state.comparePages,
        currentComparePage: (state) => state.currentComparePage,
        loading: (state) => state.loading,
        error: (state) => state.error
    },
    mutations: {
        SET_COMPARE_PAGES(state, comparePages) {
            state.comparePages = comparePages
        },
        SET_CURRENT_COMPARE_PAGE(state, comparePage) {
            state.currentComparePage = comparePage
        },
        ADD_COMPARE_PAGE(state, comparePage) {
            state.comparePages.push(comparePage)
        },
        UPDATE_COMPARE_PAGE(state, updatedPage) {
            const index = state.comparePages.findIndex(page => page.id === updatedPage.id)
            if (index !== -1) {
                state.comparePages.splice(index, 1, updatedPage)
            }
        },
        REMOVE_COMPARE_PAGE(state, id) {
            state.comparePages = state.comparePages.filter(page => page.id !== id)
        },
        SET_LOADING(state, status) {
            state.loading = status
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        }
    },
    actions: {
        // Fetch all comparison pages
        async fetchComparePages({ commit }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                // Using axios through our api instance
                const response = await api.get('/candidate-compare')

                commit('SET_COMPARE_PAGES', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch comparison pages'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch a single comparison page by ID
        async fetchComparePageById({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/candidate-compare/${id}`)

                commit('SET_CURRENT_COMPARE_PAGE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Create a new comparison page
        async createComparePage({ commit }, pageData) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post('/candidate-compare', pageData)

                commit('ADD_COMPARE_PAGE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to create comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Update an existing comparison page
        async updateComparePage({ commit }, { id, pageData }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.put(`/candidate-compare/${id}`, pageData)

                commit('UPDATE_COMPARE_PAGE', response.data.data)
                commit('SET_CURRENT_COMPARE_PAGE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Duplicate an existing comparison page
        async duplicateComparePage({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post(`/candidate-compare/${id}/duplicate`)

                commit('ADD_COMPARE_PAGE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to duplicate comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Delete a comparison page
        async deleteComparePage({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                await api.delete(`/candidate-compare/${id}`)

                commit('REMOVE_COMPARE_PAGE', id)
                return true
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to delete comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch a comparison page for public viewing
        async fetchPublicComparePage({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const url = `/public/candidate-compare/${id}`;

                const response = await api.get(url)

                commit('SET_CURRENT_COMPARE_PAGE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch comparison page'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        }
    }
}
